/**
 * 客户端系统时钟纠偏
 *
 * 背景：客户端系统时间可能与服务端存在较大偏移（用户手动改错系统时间、
 * 未开启 NTP 同步等），此时裸用 `Date.now()` 计算「距离创建时间过了多久」
 * 一类的倒计时会长期不准（例如撤回冷静期倒计时卡住不消失）。
 *
 * 做法：后端统一响应格式（RFC135）每个响应体都带 `timestamp` 字段。
 * 每次收到响应时用它和本机收到响应的时刻做差，得到偏移量 `skewMs`，
 * 之后所有需要「当前时间」的计算都应使用 `getCorrectedNowMs()`
 * （= `Date.now() + skewMs`），而不是直接使用 `Date.now()`。
 */

let clockSkewMs = 0;

/**
 * 用后端响应的 timestamp 更新本机与服务端的时钟偏移。
 *
 * @param serverTimestamp 后端统一响应体中的 timestamp（ISO 字符串或 epoch ms）
 * @param receivedAtMs 本机收到该响应时的 `Date.now()`，默认取当前时刻
 */
export function updateServerClockSkew(
  serverTimestamp: unknown,
  receivedAtMs: number = Date.now()
): void {
  if (typeof serverTimestamp !== 'string' && typeof serverTimestamp !== 'number') return;

  const serverMs =
    typeof serverTimestamp === 'number' ? serverTimestamp : Date.parse(serverTimestamp);
  if (!Number.isFinite(serverMs)) return;

  clockSkewMs = serverMs - receivedAtMs;
}

/** 当前已知的客户端-服务端时钟偏移（毫秒），正值表示本机时钟落后服务端 */
export function getClockSkewMs(): number {
  return clockSkewMs;
}

/** 纠偏后的「当前时间」，倒计时等场景应使用它代替 `Date.now()` */
export function getCorrectedNowMs(): number {
  return Date.now() + clockSkewMs;
}

/** 仅供测试使用：重置偏移量 */
export function resetClockSkewForTests(): void {
  clockSkewMs = 0;
}
